import { useMemo, useState } from 'react';

import type { CrmAccount, Crm } from '../../../../domain/crm';
import { useCrmSearchAccountsQuery } from '../../../../features/crm/previewModal/useCrmSearchAccountsQuery';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useObserveIntersection } from '../../../../hooks/useObserveIntersection';
import { NEW_ACCOUNT_VALUE } from '../CrmExportContainer/CrmExportContainer';

export type CrmExportPreviewModalProps = {
  crm: Crm;
  isOpen: boolean;
  setSelectedAccount: (account: CrmAccount) => void;
  trackExportEvent: (action: string, eventSubName?: string) => void;
  matchedCompanies?: CrmAccount[];
};

export const useCrmExportPreviewAccountSelect = ({
  crm,
  matchedCompanies,
  isOpen,
  setSelectedAccount,
  trackExportEvent,
}: CrmExportPreviewModalProps) => {
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchTextDebounced, setSearchTextDebounced] = useState('');

  const searchWords = searchText.toLowerCase().split(' ');
  const suggestedAccounts = useMemo(
    () =>
      searchText && matchedCompanies
        ? matchedCompanies.filter((account) => searchWords.every((word) => account.name?.toLowerCase().includes(word)))
        : matchedCompanies || [],
    [searchText, matchedCompanies]
  );

  const {
    data: accountsData,
    isFetchingNextPage,
    isLoading: isAllAccountsLoading,
    hasNextPage,
    fetchNextPage,
  } = useCrmSearchAccountsQuery({ searchText: searchTextDebounced, crm, enabled: isOpen });

  const accountsDataPages = accountsData?.pages ?? [];
  const allAccounts = accountsDataPages.flatMap((responseData) => responseData.data);

  const hasAccounts = !!allAccounts.length;

  const getSentinelElementSelector = () => {
    const accountId = allAccounts[allAccounts.length - 3]?.id ?? '';
    return `[data-account-id="${accountId}"]`;
  };

  useObserveIntersection({
    sentinelSelector: getSentinelElementSelector(),
    shouldObserve: isDropdownOpened && hasAccounts && !!hasNextPage && !isFetchingNextPage,
    onIntersection: fetchNextPage as () => void,
  });

  const handleDebouncedSearchText = useDebounce(setSearchTextDebounced, 600);

  const handleSearchText = (searchText: string) => {
    setSearchText(searchText);
    handleDebouncedSearchText(searchText);
  };

  const handleCloseDropdown = () => {
    setIsDropdownOpened(false);
  };

  const handleSelectAccount = (account: CrmAccount) => {
    setSelectedAccount(account);
    setIsDropdownOpened(false);
    trackExportEvent(
      account.id === NEW_ACCOUNT_VALUE ? 'create new account for contact' : 'select existing account for contact'
    );
  };

  return {
    suggestedAccounts,
    isFetchingNextPage,
    isAllAccountsLoading,
    allAccounts,
    handleCloseDropdown,
    handleSelectAccount,
    handleSearchText,
    isDropdownOpened,
    setIsDropdownOpened,
    searchText,
  };
};
